import { useEffect, useState } from 'react';
import { contactDisplayName } from '@taki/data-model';
import type { Contact, Project, Task } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';

/**
 * CE QUE DEMO TOWN LIT, ET D'OU. L'app n'a aucune donnee a elle : elle lit
 * trois familles du socle, que d'autres apps montrent (Contact, Projets), et
 * toujours AVEC leur type, sans quoi une derogation d'acces sur la famille fait
 * refuser la requete en bloc (`docs/DONNEES-SDK.md`, « Les membres et l'acces »).
 *
 * TOUT CHIFFRE AFFICHE VIENT DU SERVEUR (`count`) : les etages d'un immeuble,
 * les statistiques de la ville et du bureau. Les listes ne servent qu'a dessiner
 * ce qu'on voit, et elles sont bornees.
 */

export const JEUX = {
  societe: { family: 'contacts', type: 'organization' },
  personne: { family: 'contacts', type: 'person' },
  projet: { family: 'projects', type: 'projet' },
  tache: { family: 'tasks', type: 'tache' },
} as const;

export type JeuDeDemoTown = (typeof JEUX)[keyof typeof JEUX];

/** Les chemins de stockage des trois liens, tels que Contact et Projets les ecrivent. */
export const CHEMINS = {
  societeDePersonne: 'customFields.companyId',
  clientDeProjet: 'contactId',
  projetDeTache: 'projectId',
} as const;

/** Au-dela, la ville ne dessine plus d'immeuble (et le dit). */
export const IMMEUBLES_MAX = 400;
/** Les projets d'une societe lus pour son bureau (et pour son total de taches). */
const PROJETS_DU_BUREAU_MAX = 60;
/** Les personnes dessinees dans un bureau. */
const PERSONNES_DU_BUREAU_MAX = 24;
/** Les post-it lus par tableau : un de plus que ce qu'on dessine, pour savoir s'il en reste. */
const TACHES_PAR_TABLEAU = 7;
/** Requetes d'agregat en vol en meme temps. */
const EN_PARALLELE = 6;

export interface Societe {
  id: string;
  nom: string;
  ville: string | null;
  creeLe: number;
}

export interface Personne {
  id: string;
  nom: string;
  poste: string | null;
  email: string | null;
}

export interface Projet {
  id: string;
  nom: string;
}

export interface Tache {
  id: string;
  nom: string;
  projetId: string;
}

function texteDe(sac: Record<string, unknown> | undefined, clef: string): string | null {
  const valeur = sac?.[clef];
  return typeof valeur === 'string' && valeur.trim() !== '' ? valeur.trim() : null;
}

function versSociete(c: Contact): Societe {
  return { id: c.id, nom: contactDisplayName(c), ville: texteDe(c.customFields, 'city'), creeLe: c.createdAt.getTime() };
}

function versPersonne(c: Contact): Personne {
  return { id: c.id, nom: contactDisplayName(c), poste: texteDe(c.customFields, 'jobPosition'), email: c.email ?? null };
}

/* Le titre d'un projet et d'une tache est le champ sur-mesure `libelle` de l'app Projets. */
function versProjet(p: Project): Projet {
  return { id: p.id, nom: texteDe(p.customFields, 'libelle') ?? 'Projet sans nom' };
}

function versTache(t: Task): Tache {
  return { id: t.id, nom: texteDe(t.customFields, 'libelle') ?? 'Tâche sans nom', projetId: t.projectId ?? '' };
}

/** Lit page apres page jusqu'a la fin ou au plafond. `tronque` dit qu'il en restait. */
async function toutLire<T extends { id: string }>(
  lirePage: (apres: string | undefined) => Promise<T[]>,
  plafond: number,
): Promise<{ fiches: T[]; tronque: boolean }> {
  const fiches: T[] = [];
  const page = Math.min(100, plafond);
  let apres: string | undefined;
  for (;;) {
    const lues = await lirePage(apres);
    fiches.push(...lues);
    if (lues.length < page) return { fiches, tronque: false };
    if (fiches.length >= plafond) return { fiches: fiches.slice(0, plafond), tronque: true };
    apres = lues[lues.length - 1]?.id;
  }
}

/** Applique `f` a chaque element, `EN_PARALLELE` a la fois, et rend chaque lot des qu'il est pret. */
async function parLots<T, R>(elements: readonly T[], f: (e: T) => Promise<R>, lot: (r: Array<[T, R]>) => void): Promise<void> {
  for (let i = 0; i < elements.length; i += EN_PARALLELE) {
    const tranche = elements.slice(i, i + EN_PARALLELE);
    const resultats = await Promise.all(tranche.map(async (e) => [e, await f(e)] as [T, R]));
    lot(resultats);
  }
}

function messageDe(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/* ------------------------------------------------------------------ */
/* LA VILLE                                                            */
/* ------------------------------------------------------------------ */

export interface EtatDeLaVille {
  societes: Societe[] | null;
  /** Il existe plus de societes que la ville n'en dessine. */
  tronque: boolean;
  /** Les etages de chaque immeuble : le nombre de projets de la societe, compte au serveur. */
  etages: Readonly<Record<string, number>>;
  totalSocietes: number | null;
  totalProjets: number | null;
  erreur: string | null;
}

export function useVille(sdk: TakiSdk): EtatDeLaVille {
  const [etat, setEtat] = useState<EtatDeLaVille>({
    societes: null,
    tronque: false,
    etages: {},
    totalSocietes: null,
    totalProjets: null,
    erreur: null,
  });

  useEffect(() => {
    let vivant = true;
    const poser = (patch: Partial<EtatDeLaVille>): void => {
      if (vivant) setEtat((avant) => ({ ...avant, ...patch }));
    };
    void (async () => {
      try {
        const [lues, totalSocietes, totalProjets] = await Promise.all([
          toutLire(
            (apres) => sdk.contacts.list({ type: JEUX.societe.type, limit: 100, ...(apres ? { after: apres } : {}) }),
            IMMEUBLES_MAX,
          ),
          sdk.contacts.count({ type: JEUX.societe.type }),
          sdk.projects.count({ type: JEUX.projet.type }),
        ]);
        /* LES PLUS ANCIENNES AU CENTRE : une societe nouvelle s'ajoute en bord de
           ville, et aucun immeuble existant ne bouge. */
        const societes = lues.fiches.map(versSociete).sort((a, b) => a.creeLe - b.creeLe || a.id.localeCompare(b.id));
        poser({ societes, tronque: lues.tronque, totalSocietes, totalProjets });
        await parLots(
          societes,
          (s) => sdk.projects.count({ type: JEUX.projet.type, where: { [CHEMINS.clientDeProjet]: s.id } }),
          (lot) => {
            if (!vivant) return;
            setEtat((avant) => {
              const etages = { ...avant.etages };
              for (const [s, n] of lot) etages[s.id] = n;
              return { ...avant, etages };
            });
          },
        );
      } catch (cause) {
        poser({ erreur: `La ville ne se lit pas : ${messageDe(cause)}` });
      }
    })();
    return () => {
      vivant = false;
    };
  }, [sdk]);

  return etat;
}

/* ------------------------------------------------------------------ */
/* L'APERCU D'UNE SOCIETE (la bulle de la ville)                       */
/* ------------------------------------------------------------------ */

export interface ApercuDeSociete {
  projets: Projet[];
  personnes: Personne[];
  totalPersonnes: number | null;
  erreur: string | null;
}

const LIGNES_D_APERCU = 5;

export function useApercu(sdk: TakiSdk, societeId: string | null): ApercuDeSociete | null {
  const [apercu, setApercu] = useState<{ id: string; valeur: ApercuDeSociete } | null>(null);

  useEffect(() => {
    if (societeId === null) return;
    let vivant = true;
    void (async () => {
      const [projets, personnes, totalPersonnes] = await Promise.allSettled([
        sdk.projects.list({ type: JEUX.projet.type, where: { [CHEMINS.clientDeProjet]: societeId }, limit: LIGNES_D_APERCU }),
        sdk.contacts.list({ type: JEUX.personne.type, where: { [CHEMINS.societeDePersonne]: societeId }, limit: LIGNES_D_APERCU }),
        sdk.contacts.count({ type: JEUX.personne.type, where: { [CHEMINS.societeDePersonne]: societeId } }),
      ]);
      if (!vivant) return;
      const refus = [projets, personnes, totalPersonnes].find((r) => r.status === 'rejected');
      setApercu({
        id: societeId,
        valeur: {
          projets: projets.status === 'fulfilled' ? projets.value.map(versProjet) : [],
          personnes: personnes.status === 'fulfilled' ? personnes.value.map(versPersonne) : [],
          totalPersonnes: totalPersonnes.status === 'fulfilled' ? totalPersonnes.value : null,
          erreur: refus?.status === 'rejected' ? `Lecture incomplète : ${messageDe(refus.reason)}` : null,
        },
      });
    })();
    return () => {
      vivant = false;
    };
  }, [sdk, societeId]);

  return apercu !== null && apercu.id === societeId ? apercu.valeur : null;
}

/* ------------------------------------------------------------------ */
/* LE BUREAU (l'interieur d'un immeuble)                               */
/* ------------------------------------------------------------------ */

export interface EtatDuBureau {
  chargement: boolean;
  /** La societe n'existe pas, plus, ou n'est pas lisible. */
  introuvable: boolean;
  societe: Societe | null;
  personnes: Personne[];
  projets: Projet[];
  /** Les post-it de chaque tableau, `TACHES_PAR_TABLEAU` au plus. */
  taches: Readonly<Record<string, Tache[]>>;
  /** Le nombre de taches de chaque projet, compte au serveur. */
  tachesParProjet: Readonly<Record<string, number>>;
  totalPersonnes: number | null;
  totalProjets: number | null;
  /** La somme des taches des projets lus ; `tachesPartiel` dit qu'il manque des projets. */
  totalTaches: number | null;
  tachesPartiel: boolean;
  erreur: string | null;
}

const BUREAU_VIDE: EtatDuBureau = {
  chargement: true,
  introuvable: false,
  societe: null,
  personnes: [],
  projets: [],
  taches: {},
  tachesParProjet: {},
  totalPersonnes: null,
  totalProjets: null,
  totalTaches: null,
  tachesPartiel: false,
  erreur: null,
};

export function useBureau(sdk: TakiSdk, societeId: string): EtatDuBureau {
  const [etat, setEtat] = useState<EtatDuBureau>(BUREAU_VIDE);

  useEffect(() => {
    let vivant = true;
    setEtat(BUREAU_VIDE);
    void (async () => {
      const contact = await sdk.contacts.get(societeId).catch(() => null);
      if (!vivant) return;
      if (contact === null || contact.type !== JEUX.societe.type) {
        setEtat({ ...BUREAU_VIDE, chargement: false, introuvable: true });
        return;
      }
      const sousLaSociete = { [CHEMINS.societeDePersonne]: societeId };
      const duClient = { [CHEMINS.clientDeProjet]: societeId };
      const [personnes, totalPersonnes, projets, totalProjets] = await Promise.allSettled([
        sdk.contacts.list({ type: JEUX.personne.type, where: sousLaSociete, limit: PERSONNES_DU_BUREAU_MAX }),
        sdk.contacts.count({ type: JEUX.personne.type, where: sousLaSociete }),
        toutLire(
          (apres) => sdk.projects.list({ type: JEUX.projet.type, where: duClient, limit: 100, ...(apres ? { after: apres } : {}) }),
          PROJETS_DU_BUREAU_MAX,
        ),
        sdk.projects.count({ type: JEUX.projet.type, where: duClient }),
      ]);
      if (!vivant) return;
      const refus = [personnes, totalPersonnes, projets, totalProjets].find((r) => r.status === 'rejected');
      const lesProjets = projets.status === 'fulfilled' ? projets.value.fiches.map(versProjet) : [];
      setEtat({
        ...BUREAU_VIDE,
        chargement: false,
        societe: versSociete(contact),
        personnes: personnes.status === 'fulfilled' ? personnes.value.map(versPersonne) : [],
        projets: lesProjets,
        totalPersonnes: totalPersonnes.status === 'fulfilled' ? totalPersonnes.value : null,
        totalProjets: totalProjets.status === 'fulfilled' ? totalProjets.value : null,
        tachesPartiel: projets.status === 'fulfilled' && projets.value.tronque,
        erreur: refus?.status === 'rejected' ? `Lecture incomplète : ${messageDe(refus.reason)}` : null,
      });

      /* LES TACHES, PROJET PAR PROJET : le compte de chacun (au serveur) fait le
         total du bureau, et ses premieres taches font les post-it du tableau. */
      let somme = 0;
      try {
        await parLots(
          lesProjets,
          async (p) => {
            const ceProjet = { [CHEMINS.projetDeTache]: p.id };
            const [nombre, premieres] = await Promise.all([
              sdk.tasks.count({ type: JEUX.tache.type, where: ceProjet }),
              sdk.tasks.list({ type: JEUX.tache.type, where: ceProjet, limit: TACHES_PAR_TABLEAU }),
            ]);
            return { nombre, premieres: premieres.map(versTache) };
          },
          (lot) => {
            if (!vivant) return;
            for (const [, r] of lot) somme += r.nombre;
            setEtat((avant) => {
              const taches = { ...avant.taches };
              const tachesParProjet = { ...avant.tachesParProjet };
              for (const [p, r] of lot) {
                taches[p.id] = r.premieres;
                tachesParProjet[p.id] = r.nombre;
              }
              return { ...avant, taches, tachesParProjet };
            });
          },
        );
        if (vivant) setEtat((avant) => ({ ...avant, totalTaches: somme }));
      } catch (cause) {
        if (vivant) setEtat((avant) => ({ ...avant, erreur: `Les tâches ne se lisent pas : ${messageDe(cause)}` }));
      }
    })();
    return () => {
      vivant = false;
    };
  }, [sdk, societeId]);

  return etat;
}
